import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Plus, Minus, ShieldCheck, Lock, FileCheck, MapPin } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";
import { ENGINE_LANDING_PAGES, ENGINE_LANDING_LIST } from "@/content/engineLanding";
import { ENGINES } from "@/content/engines";

const BASE = "https://specpixel.com";

export default function EngineLandingPage({ engineKey }) {
  const page = ENGINE_LANDING_PAGES[engineKey];
  const [openFaq, setOpenFaq] = useState(null);
  if (!page) return <Navigate to="/services" replace />;

  const eng = ENGINES[page.engine] || page.engineMeta;
  const EngineIcon = eng.icon;
  const others = ENGINE_LANDING_LIST.filter((p) => p.slug !== page.slug);
  const labelOf = (p) => (ENGINES[p.engine] || p.engineMeta).label;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.h1,
    description: page.seoDescription,
    url: `${BASE}/${page.slug}`,
    provider: {
      "@type": "Organization",
      name: "Specpixel Entertainment",
      url: BASE,
      email: "business@specpixel.com",
      address: { "@type": "PostalAddress", addressLocality: "Melbourne", addressRegion: "VIC", addressCountry: "AU" },
    },
    areaServed: "Worldwide",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: page.h1,
      itemListElement: page.offerings.map((o) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: o.title, description: o.desc },
      })),
    },
  };
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${BASE}/services` },
      { "@type": "ListItem", position: 3, name: page.h1, item: `${BASE}/${page.slug}` },
    ],
  };

  return (
    <div data-testid={`engine-landing-${engineKey}`} className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col selection:bg-nc-blue/30">
      <Seo title={page.seoTitle} description={page.seoDescription} keywords={page.keywords} path={`/${page.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <SiteNav />

      <header className="border-b border-white/10 relative overflow-hidden">
        <div className="absolute inset-0 grid-texture opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-24">
          <nav data-testid="engine-breadcrumb" className="font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-600 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-nc-blue transition-colors">Home</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-nc-blue transition-colors">Services</Link>
            <span>/</span>
            <span className="text-zinc-400">{eng.label}</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-center gap-3 mb-5">
              <span className={`w-10 h-10 border flex items-center justify-center ${eng.accent}`}>
                <EngineIcon className="w-5 h-5" strokeWidth={1.75} />
              </span>
              <p className="font-mono text-xs tracking-[0.4em] text-nc-blue uppercase">Development Services</p>
            </div>
            <h1 data-testid="engine-landing-title" className="font-sans font-bold uppercase tracking-tight text-3xl sm:text-5xl lg:text-6xl text-zinc-50 max-w-4xl">
              {page.h1}
            </h1>
            <p className="font-mono text-sm text-nc-orange mt-5 max-w-2xl">{page.tagline}</p>
            {page.intro.map((p, i) => (
              <p key={i} className="font-mono text-sm text-zinc-400 mt-4 max-w-3xl leading-relaxed">{p}</p>
            ))}
            <div className="mt-6 flex flex-wrap gap-2">
              {eng.versions.map((v) => (
                <span key={v} className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 border border-white/15 px-3 py-1.5">{v}</span>
              ))}
              {eng.frameworks.map((f) => (
                <span key={f} className="font-mono text-[10px] tracking-[0.2em] text-nc-orange border border-nc-orange/40 bg-nc-orange/10 px-3 py-1.5">{f}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="engine-quote-cta"
                className="font-mono text-xs tracking-[0.25em] uppercase bg-nc-blue text-white px-6 py-3.5 hover:bg-nc-blue/80 transition-colors duration-300 inline-flex items-center gap-2"
              >
                Get a Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/portfolio"
                data-testid="engine-portfolio-cta"
                className="font-mono text-xs tracking-[0.25em] uppercase border border-white/20 text-zinc-200 px-6 py-3.5 hover:border-nc-blue hover:text-nc-blue transition-colors duration-300"
              >
                View Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 w-full">
        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mb-2">
          What we deliver in {eng.label}
        </h2>
        <p className="font-mono text-xs text-zinc-500 mb-8">Fixed-scope milestones. Source-complete handoff. Escrow-protected payment.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {page.offerings.map((o, i) => (
            <motion.div
              key={o.title}
              data-testid={`engine-offering-${i}`}
              className="border border-white/10 bg-black/40 p-6 hover:border-white/30 transition-colors duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
            >
              <h3 className="font-sans font-bold uppercase text-base text-zinc-100">{o.title}</h3>
              <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed">{o.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 border border-white/10 bg-black/40 p-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: ShieldCheck, t: "Identity-Verified", d: "Every contractor is ID-verified and clearance-rated on delivered work." },
            { icon: Lock, t: "Escrow-Protected", d: "Funds secured upfront, released only when you approve the delivery." },
            { icon: FileCheck, t: "Source-Complete", d: "Full source files, documentation and IP transfer on every engagement." },
            { icon: MapPin, t: "Australian Studio", d: "ABN-registered Melbourne studio covered by EULA and mutual NDA." },
          ].map((b) => (
            <div key={b.t} data-testid={`engine-trust-${b.t.toLowerCase().replace(/\s+/g, "-")}`}>
              <b.icon className="w-5 h-5 text-nc-blue mb-3" strokeWidth={1.75} />
              <p className="font-sans font-bold uppercase text-sm text-zinc-100">{b.t}</p>
              <p className="font-mono text-[11px] text-zinc-500 mt-2 leading-relaxed">{b.d}</p>
            </div>
          ))}
        </div>

        <h2 className="font-sans font-bold uppercase tracking-tight text-2xl sm:text-3xl text-zinc-50 mt-16 mb-8">
          Frequently asked questions
        </h2>
        <div className="flex flex-col gap-3 max-w-4xl">
          {page.faqs.map((f, i) => {
            const open = openFaq === i;
            return (
              <div key={i} data-testid={`engine-faq-${i}`} className={`border transition-colors duration-300 ${open ? "border-nc-blue/60 bg-nc-blue/5" : "border-white/10 bg-black/40"}`}>
                <button
                  data-testid={`engine-faq-toggle-${i}`}
                  onClick={() => setOpenFaq(open ? null : i)}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left"
                >
                  <span className="font-sans font-bold text-sm text-zinc-100 flex-1 normal-case">{f.q}</span>
                  {open ? <Minus className="w-4 h-4 text-nc-blue shrink-0" /> : <Plus className="w-4 h-4 text-zinc-500 shrink-0" />}
                </button>
                <AnimatePresence>
                  {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                      <p className="px-5 pb-5 font-mono text-xs text-zinc-400 leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <p className="font-mono text-[10px] tracking-[0.3em] text-zinc-600 uppercase mb-4">Explore other engine services</p>
          <div className="flex flex-wrap gap-2">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`engine-crosslink-${p.engine}`}
                className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400 border border-white/15 px-3 py-2 hover:border-nc-blue hover:text-nc-blue transition-colors duration-300"
              >
                {labelOf(p)}
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-16 border border-nc-blue/30 bg-nc-blue/5 p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-1">
            <h2 className="font-sans font-bold uppercase tracking-tight text-xl sm:text-2xl text-zinc-50">
              Start your {eng.label} project
            </h2>
            <p className="font-mono text-xs text-zinc-400 mt-3 leading-relaxed max-w-xl">
              Send us a project brief and receive a scoped, fixed-price quote within 24 hours. Confidential from first contact.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="engine-bottom-cta"
            className="shrink-0 font-mono text-xs tracking-[0.25em] uppercase bg-nc-orange text-black px-6 py-3.5 hover:bg-nc-orange/80 transition-colors duration-300 inline-flex items-center gap-2"
          >
            Request a Quote <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
